import { useEffect, useRef, useState } from 'react';
import { getMediaDevices, getPreferredAudioInputId, pollAudioLevel } from '@streem/toolbox';
import { Option } from '@streem/ui-react';

import appLogger from '../util/logging/app_logger';
import { normalizeAudioLevel } from '@streem/sdk-react';
import { cleanDeviceLabel } from '../util/clean_device_labels';

const log = appLogger.extend('AudioInputSelect');

export type AudioInputOption = Option<string>;

const convertMediaDeviceInfoToAudioInputOption = (device: MediaDeviceInfo): AudioInputOption => ({
    value: device.deviceId,
    label: cleanDeviceLabel(device.label),
});

interface UseAudioPermissionAndDevicesProps {
    open: boolean;
    onPermissionDenied: () => void;
    setAudioInputDevices: (props: AudioInputOption[]) => void;
    onPermissionAllowed: () => void;
    setPlaceholder: (message: string) => void;
}

export const useSetUpMediaStreamAndDevices = ({
    open,
    onPermissionDenied,
    onPermissionAllowed,
    setAudioInputDevices,
    setPlaceholder,
}: UseAudioPermissionAndDevicesProps): MediaStream => {
    const stream = useRef<MediaStream>();
    const preferredAudioInputId = getPreferredAudioInputId();

    useEffect(() => {
        const setUpDeviceList = async () => {
            const devices = await getMediaDevices('audioinput');
            const options = devices.map(convertMediaDeviceInfoToAudioInputOption);
            setAudioInputDevices(options);

            if (options.length > 0) {
                setPlaceholder('Select Microphone');
            }
        };

        const getAudioMediaStream = async () => {
            try {
                stream.current = await navigator.mediaDevices.getUserMedia({
                    audio: preferredAudioInputId
                        ? { deviceId: { exact: preferredAudioInputId } }
                        : true,
                });

                onPermissionAllowed();

                await setUpDeviceList();
            } catch (e) {
                log.warn('call to getUserMedia failed for audio input', e);
                onPermissionDenied();
            }
        };

        if (open) {
            getAudioMediaStream();
        }

        return () => {
            if (stream.current) {
                stream.current.getAudioTracks().forEach(track => track.stop());
            }
        };
    }, [
        open,
        onPermissionDenied,
        onPermissionAllowed,
        setAudioInputDevices,
        setPlaceholder,
        preferredAudioInputId,
    ]);

    return stream.current;
};

export const useAudioLevelPolling = (
    mediaStream: MediaStream | null,
): { audioLevel: number; enabledYn: boolean } => {
    const [audioLevel, setAudioLevel] = useState(0);
    const [enabledYn, setEnabledYn] = useState(false);

    useEffect(() => {
        if (!mediaStream) return;

        const liveTrack = mediaStream.getAudioTracks()[0];

        pollAudioLevel({
            track: liveTrack,
            onLevelChanged(level) {
                if (liveTrack.readyState !== 'ended') {
                    setAudioLevel(normalizeAudioLevel(level));
                }
            },
            onError(message, err) {
                log.error(message, err);
            },
        });

        setEnabledYn(true);

        return () => {
            mediaStream.getAudioTracks().forEach(track => track.stop());
        };
    }, [mediaStream]);

    return { audioLevel, enabledYn };
};
